/**
 * Level-aware "reach" derivation for the Overrides editor — adapts the
 * Claude Design CS concept (binary Locked/Open + a You→…→Worker reach chain
 * shown, not chosen) to the real editor, which runs at customer / group / team.
 *
 * The design was customer-only ("You" = customer). Here the current editing
 * level is variable, so the chain is "<current level> + its descendants", and
 * reach derives from each setting's live `allowedLevels` from the catalog.
 *
 * Honest model (unchanged from the design): a setting is Locked (frozen for
 * everyone below the current level) or Open (descendants the setting supports
 * may override the value). You don't pick the depth — `allowedLevels` decides
 * how far Open reaches.
 */

import type { EditableLevel, SettingsLevel } from './v2-settings-api';

// Full hierarchy order. 'user' is shown as "Worker" in this UI.
const ORDER: SettingsLevel[] = ['system', 'customer', 'group', 'team', 'user'];
const LABEL: Record<SettingsLevel, string> = {
  system: 'System',
  customer: 'Customer',
  group: 'Group',
  team: 'Team',
  user: 'Worker',
};

export type ReachNodeState = 'current' | 'reach' | 'frozen' | 'na';

export interface ReachNode {
  level: SettingsLevel;
  label: string;
  state: ReachNodeState;
  lock: boolean;       // lock badge on the current node when locked here
  showConn: boolean;   // render a connector after this node
  connBright: boolean; // connector is brand-colored (live reach) vs grey
}

export interface ReachResult {
  chain: ReachNode[];
  /** Descendant levels this setting permits — i.e. who Open delegates to. */
  delegable: SettingsLevel[];
  /** False when no descendant level is allowed: the setting is org-only here. */
  canDelegate: boolean;
  caption: string;
  captionTone: 'brand' | 'muted';
}

// Levels strictly below `level` in the hierarchy.
const descendantsOf = (level: EditableLevel): SettingsLevel[] => {
  const idx = ORDER.indexOf(level);
  return ORDER.slice(idx + 1);
};

// "groups & teams", "teams, workers", etc. for the Open caption.
export const reachWords = (levels: SettingsLevel[]): string => {
  const map: Partial<Record<SettingsLevel, string>> = { group: 'groups', team: 'teams', user: 'workers' };
  const w = levels.map((l) => map[l]).filter(Boolean) as string[];
  if (w.length === 0) return '';
  if (w.length === 1) return w[0];
  return `${w.slice(0, -1).join(', ')} & ${w[w.length - 1]}`;
};

export const deriveReach = (opts: {
  level: EditableLevel;
  allowedLevels: string[];
  lockedHere: boolean;
  /** Display value ("On", "28 min", "Call EMS") to lead the caption with,
   *  matching the value+scope framing. Omit for value-agnostic captions. */
  valueLabel?: string;
}): ReachResult => {
  const { level, allowedLevels, lockedHere, valueLabel } = opts;
  const descendants = descendantsOf(level);
  const delegable = descendants.filter((l) => allowedLevels.includes(l));
  const canDelegate = delegable.length > 0;

  const chainLevels: SettingsLevel[] = [level, ...descendants];
  const chain: ReachNode[] = chainLevels.map((lvl, i) => {
    let state: ReachNodeState;
    if (i === 0) state = 'current';
    else if (!allowedLevels.includes(lvl)) state = 'na';
    else if (lockedHere) state = 'frozen';
    else state = 'reach';

    const next = chainLevels[i + 1];
    let nextState: ReachNodeState | 'end' = 'end';
    if (next) {
      if (!allowedLevels.includes(next)) nextState = 'na';
      else if (lockedHere) nextState = 'frozen';
      else nextState = 'reach';
    }
    const connBright = (state === 'current' || state === 'reach') && nextState === 'reach';

    return {
      level: lvl,
      label: LABEL[lvl],
      state,
      lock: i === 0 && lockedHere,
      showConn: i < chainLevels.length - 1,
      connBright,
    };
  });

  // Value+scope framing: lead with the value when we have one, then state the
  // scope in plain language. "Same for everyone" replaces the misleading
  // "locked/Everyone" wording (which read as inclusive but means forced).
  let caption: string;
  let captionTone: 'brand' | 'muted';
  if (!canDelegate) {
    caption = valueLabel
      ? `${valueLabel} · set for the whole org.`
      : 'Set for the whole org — no lower level supports this setting.';
    captionTone = 'muted';
  } else if (lockedHere) {
    caption = valueLabel
      ? `${valueLabel} · same for everyone — ${reachWords(delegable)} can’t change it.`
      : `Same for everyone — ${reachWords(delegable)} can’t change it.`;
    captionTone = 'brand';
  } else {
    caption = valueLabel
      ? `${valueLabel} to start — ${reachWords(delegable)} can set their own.`
      : `Your value to start — ${reachWords(delegable)} can set their own.`;
    captionTone = 'muted';
  }

  return { chain, delegable, canDelegate, caption, captionTone };
};
